import { DeleteOutlined, EditOutlined, MinusCircleOutlined, PlusOutlined, PushpinOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Col, Descriptions, Drawer, Empty, Form, Image, Input, message, Modal, Radio, Row, Segmented, Select, Space, Tag, Timeline, Typography } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import { diff, flattenChangeset } from 'json-diff-ts';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import ReactTimeago from 'react-timeago';
import { AssetApprovalStatus, AssetApprovalStatusLabels, AssetApprovalStatusOptions, AssetSourceType } from '../../models/enums';
import { AssetSelector } from '../Inputs/AssetSelector';
import ImageFileUpload from '../Inputs/ImageFileUpload';
import { ModelpackSelector } from '../Inputs/ModelpackSelector';
import QueryField from '../Inputs/QueryField';
import { UserSelector } from '../Inputs/UserSelector';
import ApproveModel from './ApproveModel';
import { useClientServiceGetBackendv2Client, useModelServiceGetBackendv2ModelQAimages, useModelServicePutBackendv2Model } from '../../backendapi/queries';
import { AssetApprovalHistoryDTO, ModelService, ModelViewDTO, WhitemodelHotspotDTO } from '../../backendapi/requests';
import { ConfigContext } from '../../contexts/ConfigContext';
import { BASE_PATH } from '../../Environment';
const { Text, Title } = Typography;

const EditModel: React.FC<{ modelId?: number, isOpen: boolean, onClose: () => void }> = ({ modelId, isOpen, onClose }) => {
    const { data: clients } = useClientServiceGetBackendv2Client({}, undefined, { enabled: isOpen });
     const { currentClient } = useContext(ConfigContext)
   
   const { data: qaImages } = useModelServiceGetBackendv2ModelQAimages({ key: modelId ?? 0 }, undefined, { enabled: isOpen && modelId !== undefined });
    const { mutateAsync: updateModel, isPending } = useModelServicePutBackendv2Model();
    const [approvalOpen, setApprovalOpen] = useState(false);

    const [form] = Form.useForm<ModelViewDTO>();
    const [messageApi, contextHolder] = message.useMessage();

   // const clientId = Form.useWatch('clientId', form);
    const filename = Form.useWatch('filename', form);
    const source = Form.useWatch('sourceType', form);
    const sourcePath = Form.useWatch('sourcePath', form);
    const isWhitemodel = Form.useWatch('isWhitemodel', form);
    const title = Form.useWatch('title', form);

    const [modelImage, setModelImage] = useState("");
    const [model, setModel] = useState<ModelViewDTO | undefined>(undefined);

    const assetFilePath = useMemo(() => {
        if (clients) {
            let client = clients?.value.find(e => e.id === currentClient.id);
            if (client) {
                return client.maxModelsPath + "\\" + filename;
            }
        }
        return "";
    }, [currentClient, clients, filename]);

    const onSubmit = () => {
        form.submit();
    }

    const getThumbnailPath = useCallback(() => {
        return `${BASE_PATH}/model/${modelId}/thumbnail?rnd=` + Math.floor(Math.random() * 999999999)
    }, [modelId]);

    const onUpload = (info: UploadChangeParam) => {
        if (modelId) {
            setModelImage(getThumbnailPath())
        }
    }

    const onFinish = () => {
        if (modelId) {

            const values = form.getFieldsValue(true) as ModelViewDTO;
            values.clientId = currentClient.id;
            updateModel({ key: modelId, requestBody: values })
                .then(() => {
                    messageApi.success("Model updated");
                    onClose();
                }).catch(reason => {
                    messageApi.error(JSON.stringify(reason));
                });
        }
    };

    useEffect(() => {
        if (modelId && isOpen) {
            form.resetFields();
            ModelService.getBackendv2Model1(modelId).then(model => {
                setModel(model);
                setModelImage(getThumbnailPath());
                form.setFieldsValue({ ...model, metadata: [...model.metadata].sort((a, b) => ((a.locked ? 1 : 0) - (b.locked ? 1 : 0)) || a.name.localeCompare(b.name)) });
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelId, isOpen]);

    return (
        <>
            {contextHolder}

            <Drawer
                title={`Edit ${title}`}
                size={720}
                onClose={onClose}
                open={isOpen}
                extra={
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button onClick={onSubmit} type="primary" loading={isPending}>
                            Submit
                        </Button>
                    </Space>
                }
            >
                <Form
                    name="basic"
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}>

                    {/* <Form.Item rules={[{ required: true }]} name='clientId' label="Client">
                        <Select options={clients?.value.map(e => ({ label: e.name, value: e.id }))} />
                    </Form.Item> */}

                    <Form.Item label="Thumbnail">
                        <Space orientation='vertical'>
                            <ImageFileUpload title="Upload thumbnail" action={`${BASE_PATH}/model/${modelId}/image`} onUpload={onUpload} />
                            <Image placeholder="" width={300} src={modelImage} preview={{src: `${BASE_PATH}/model/${modelId}/image`}}/>
                        </Space>
                    </Form.Item>

                    <Form.Item label="QA Images">
                        <Space wrap>
                            {qaImages?.map(img => <Image key={img} placeholder="" width={150} src={BASE_PATH + img} />)}
                            {qaImages?.length === 0 && <Empty />}
                        </Space>
                    </Form.Item>

                    <Form.Item name="isEnabled" valuePropName="checked">
                        <Checkbox>Enabled</Checkbox>
                    </Form.Item>

                    <Form.Item name="approvalStatus" label="Status">
                        <Segmented vertical
                            options={[
                                { label: AssetApprovalStatusOptions[AssetApprovalStatus.Quote], value: AssetApprovalStatus.Quote, disabled: true },
                                { label: AssetApprovalStatusOptions[AssetApprovalStatus.Pending], value: AssetApprovalStatus.Pending, disabled: true },
                                { label: AssetApprovalStatusOptions[AssetApprovalStatus.InProduction], value: AssetApprovalStatus.InProduction },
                                { label: AssetApprovalStatusOptions[AssetApprovalStatus.Commited], value: AssetApprovalStatus.Commited },
                                { label: AssetApprovalStatusOptions[AssetApprovalStatus.New], value: AssetApprovalStatus.New },
                                { label: AssetApprovalStatusOptions[AssetApprovalStatus.Rejected], value: AssetApprovalStatus.Rejected },
                                { label: AssetApprovalStatusOptions[AssetApprovalStatus.RejectedButModified], value: AssetApprovalStatus.RejectedButModified, disabled: true },
                                { label: AssetApprovalStatusOptions[AssetApprovalStatus.RejectedPendingModification], value: AssetApprovalStatus.RejectedPendingModification, disabled: true },
                                { label: AssetApprovalStatusOptions[AssetApprovalStatus.Approved], value: AssetApprovalStatus.Approved },
                                { label: AssetApprovalStatusOptions[AssetApprovalStatus.ApprovedButModified], value: AssetApprovalStatus.ApprovedButModified, disabled: true },
                                { label: AssetApprovalStatusOptions[AssetApprovalStatus.ApprovedPendingModification], value: AssetApprovalStatus.ApprovedPendingModification, disabled: true },
                            ]}
                        />
                    </Form.Item>

                    <Form.Item label="History">
                        <Timeline items={[
                            {
                                color: 'blue',
                                content: (<>
                                    Model created <ReactTimeago date={model?.timestampCreate ?? '1970-1-1'} /> {model?.createdBy && <> by <b>{model?.createdBy}</b></>}
                                </>)
                            },
                            ...(model?.approvalHistory || []).map(e => ({
                                color: e.newStatus === AssetApprovalStatus.Approved ? 'green' : (e.newStatus === AssetApprovalStatus.Rejected ? 'red' : 'blue'),
                                content: (<>
                                    Status changed from <b>{AssetApprovalStatusOptions[e.oldStatus as AssetApprovalStatus]}</b> to <b>{AssetApprovalStatusOptions[e.newStatus as AssetApprovalStatus]}</b> {' '}
                                    <ReactTimeago date={e.timestamp} />
                                    {e.username && <> by <b>{e.username}</b></>}
                                    <ModelHistoryDetailsButton item={e} model={model} />
                                    {e.comment && <>
                                        <br />
                                        <Text code>{e.comment}</Text>
                                    </>}
                                </>)
                            }))]} />
                        <Button type='primary' onClick={() => setApprovalOpen(true)}>Start Approval</Button>
                    </Form.Item>

                    <Form.Item rules={[{ required: true }]} name='title' label="Title">
                        <Input />
                    </Form.Item>

                    <Form.Item name='producingArtistId' label="Artist">
                        <UserSelector />
                    </Form.Item>

                    <Form.Item rules={[{ required: true }]} name='complexity' label="Complexity" >
                        <Radio.Group
                            options={[
                                { label: 'Simple', value: 0 },
                                { label: 'Complex', value: 5 },
                                { label: 'Very complex', value: 10 }
                            ]}
                            optionType="button"
                            buttonStyle="solid"
                        />
                    </Form.Item>

                    <Form.Item rules={[{ required: true }]} name='sourceType' label="Source" >
                        <Radio.Group
                            options={[
                                { label: 'Unknown', value: 0 },
                                { label: 'Existing CoD asset', value: 1 },
                                { label: 'Workflow', value: 2 },
                                { label: 'None', value: 3 },
                                { label: 'CAD File', value: 4 },
                                { label: 'Max File', value: 5 }
                            ]}
                            optionType="button"
                            buttonStyle="solid"
                        />
                    </Form.Item>

                    {source === AssetSourceType.Workflow &&
                        <Form.Item label="Workflow asset">
                            <Space.Compact style={{ width: '100%' }}>
                                <Form.Item name='sourcePath' style={{ flexGrow: 1 }} noStyle>
                                    <Input addonBefore="http://" />
                                </Form.Item>
                                <Button type="default" onClick={() => window.open(sourcePath ?? "")}>Open</Button>
                            </Space.Compact>
                        </Form.Item>}

                    {source === AssetSourceType.ExistingAsset &&
                        <Form.Item label="Source model" name="sourcePath">
                            <AssetSelector type='Model' />
                        </Form.Item>}

                    {(source === AssetSourceType.CADFile || source === AssetSourceType.MaxFile) &&
                        <Form.Item label="Source file folder">
                            <Space.Compact style={{ width: '100%' }}>
                                <Form.Item name='sourcePath' style={{ flexGrow: 1 }} noStyle>
                                    <Input />
                                </Form.Item>
                                <CopyToClipboard text={sourcePath ?? ""}>
                                    <Button type="default">Copy path</Button>
                                </CopyToClipboard>
                            </Space.Compact>
                        </Form.Item>
                    }

                    <Form.Item name='notes' label="Notes">
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Form.Item label="Filename">
                        <Space.Compact style={{ width: '100%' }}>
                            <Form.Item name='filename' style={{ flexGrow: 1 }} noStyle>
                                <Input />
                            </Form.Item>
                            <CopyToClipboard text={assetFilePath}>
                                <Button type="default">Copy full path</Button>
                            </CopyToClipboard>
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item rules={[{ required: true }]} name='name' label="Name">
                        <Input />
                    </Form.Item>

                    <Form.Item name='tags' label="Tags">
                        <Select mode='tags' options={[]} />
                    </Form.Item>

                    <Form.Item label='Model packs'>
                        <Form.List name="modelpacks">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex' }} align="baseline">

                                            <Form.Item
                                                style={{ flexGrow: 1 }}
                                                {...restField}
                                                name={[name, 'id']}
                                                rules={[{ required: true }]}
                                            >
                                                <ModelpackSelector style={{ width: 350 }} />
                                            </Form.Item>
                                            <MinusCircleOutlined onClick={() => remove(name)} />
                                        </Space>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                            Add pack
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                    </Form.Item>

                    <Form.Item label='Metadata'>
                        {model?.timestampMetadataImport && <p>Metadata imported from DAM <ReactTimeago date={model?.timestampMetadataImport} /></p>}
                        <Form.List name="metadata">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex' }} align="baseline">
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'name']}
                                                style={{width: 180}}
                                                rules={[{ required: true }]}
                                            >
                                                <Input placeholder="name" disabled={form.getFieldValue(["metadata", name, "locked"])} />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                style={{width: 360}}
                                                name={[name, 'value']}
                                                rules={[{ required: true }]}
                                            >
                                                <Input placeholder="value" disabled={form.getFieldValue(["metadata", name, "locked"])} />
                                            </Form.Item>
                                            {form.getFieldValue(["metadata", name, "locked"]) !== true && <MinusCircleOutlined onClick={() => remove(name)} />}
                                        </Space>
                                    ))}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                            Add metadata
                                        </Button>
                                    </Form.Item>
                                </>
                            )}
                        </Form.List>
                    </Form.Item>

                    <Form.Item label='Surfaces'>
                        <Form.List name="surfaces">
                            {(fields) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex' }} align="baseline">
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'label']}
                                                rules={[{ required: true }]}
                                            >
                                                <Input placeholder="Label" />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'objectSelector']}
                                                rules={[{ required: true }]}
                                            >
                                                <Input readOnly disabled placeholder="object selector" />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'materialSelectionFilter']}
                                            >
                                                <QueryField placeholder="material filter" type='Material' clientId={currentClient.id} />
                                            </Form.Item>
                                        </Space>
                                    ))}
                                </>
                            )}
                        </Form.List>
                    </Form.Item>

                    <Form.Item label='Placement points'>
                        <Form.List name="placementpoints">
                            {(fields) => (
                                <>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <Space key={key} style={{ display: 'flex' }} align="baseline">
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'label']}
                                                rules={[{ required: true }]}
                                            >
                                                <Input placeholder="Label" />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'objectSelector']}
                                                rules={[{ required: true }]}
                                            >
                                                <Input readOnly disabled placeholder="object selector" />
                                            </Form.Item>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'modelSelectionFilter']}
                                            >
                                                <QueryField placeholder="model filter" type='Model' clientId={currentClient.id} />
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                name={[name, 'isRequired']}
                                                valuePropName="checked"
                                            >
                                                <Checkbox>Required</Checkbox>
                                            </Form.Item>

                                            <Form.Item
                                                {...restField}
                                                name={[name, 'useModelpack']}
                                                valuePropName="checked"
                                            >
                                                <Checkbox>Use&nbsp;modelpack</Checkbox>
                                            </Form.Item>
                                        </Space>
                                    ))}
                                </>
                            )}
                        </Form.List>
                    </Form.Item>

                    <Form.Item name="isWhitemodel" valuePropName="checked">
                        <Checkbox>Whitemodel</Checkbox>
                    </Form.Item>

                    {isWhitemodel &&
                        <Form.Item label='Hotspots'>
                            <Form.List name="hotspots">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map(({ key, name, ...restField }) => (
                                            <Space key={key} style={{ display: 'flex' }} align="baseline">
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'targetName']}
                                                    rules={[{ required: true }]}
                                                >
                                                    <Input placeholder="name" />
                                                </Form.Item>
                                                <Form.Item shouldUpdate name={name}>
                                                    <ConfigureHotspotInput model={model} />
                                                </Form.Item>
                                            </Space>
                                        ))}
                                        <Form.Item>
                                            <Button type="dashed" onClick={() => add({ posX: 0.5, posY: 0.5, rectX1: 0.45, rectX2: 0.55, rectY1: 0.45, rectY2: 0.55, targetName: '' } satisfies WhitemodelHotspotDTO)} block icon={<PlusOutlined />}>
                                                Add hotspot
                                            </Button>
                                        </Form.Item>
                                    </>
                                )}
                            </Form.List>
                        </Form.Item>
                    }
                </Form>
            </Drawer>

            <ApproveModel isOpen={approvalOpen} onClose={() => setApprovalOpen(false)} models={model === undefined ? [] : [model]} hideEditor />
        </>

    );
};

const ConfigureHotspotInput: React.FC<{ model: ModelViewDTO | undefined, value?: WhitemodelHotspotDTO, onChange?: (value: WhitemodelHotspotDTO) => void }> = ({ onChange, value, model }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [top, setTop] = useState(0);
    const [left, setLeft] = useState(0);
    const [bottom, setBottom] = useState(0);
    const [right, setRight] = useState(0);
    const [x, setX] = useState(0);
    const [y, setY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [selectedpoint, setSelectedPoint] = useState(0);

    useEffect(() => {
        if (value) {
            setLeft(Math.min(value.rectX1, value.rectX2) ?? 0.45);
            setRight(Math.max(value.rectX1, value.rectX2) ?? 0.55);
            setTop(Math.min(value.rectY1, value.rectY2) ?? 0.45);
            setBottom(Math.max(value.rectY1, value.rectY2) ?? 0.55);
            setX(value.posX);
            setY(value.posY);
        }
    }, [value]);

    const handleClose = () => {
        if (value && onChange) {
            onChange({ ...value, rectX1: left, rectX2: right, rectY1: top, rectY2: bottom, posX: x, posY: y });
        }
        setIsOpen(false)
    }

    const onHandleStartDrag = (e: React.MouseEvent, index: number) => {
        e.preventDefault();
        setIsDragging(true);
        setSelectedPoint(index);
    }

    const onHandleDrag = (e: React.MouseEvent) => {
        e.preventDefault();

        if (isDragging) {
            var rect = e.currentTarget.getBoundingClientRect();
            var mouseX = ((e.clientX - rect.left) / rect.width);
            var mouseY = ((e.clientY - rect.top) / rect.height);

            if (selectedpoint === -1) {
                setX(mouseX);
                setY(mouseY);
            }
            if (selectedpoint === 0) {
                setLeft(Math.min(mouseX, right));
                setTop(Math.min(mouseY, bottom));
            }
            if (selectedpoint === 1) {
                setRight(Math.max(mouseX, left));
                setTop(Math.min(mouseY, bottom));
            }
            if (selectedpoint === 2) {
                setLeft(Math.min(mouseX, right));
                setBottom(Math.max(mouseY, top));
            }
            if (selectedpoint === 3) {
                setRight(Math.max(mouseX, left));
                setBottom(Math.max(mouseY, top));
            }
        }
    }

    const onHandleDragEnd = (e: React.MouseEvent) => {
        if (e.preventDefault) {
            e.preventDefault();
            e.stopPropagation();
        }

        setIsDragging(false);
    }

    return (
        <Space>
            <Button title='Hotspots' onClick={() => setIsOpen(true)}><PushpinOutlined /></Button>
            <Modal title="Hotspots" open={isOpen} onOk={handleClose} onCancel={handleClose} width='90vw'>
                <div className='hotspots-editor' style={{ width: '100%', height: '75vh' }}>
                    <div className='configuration'>
                        <div className="hotspots-container">
                            <div className="hotspots">
                                <img src={`${BASE_PATH}${model?.image}`} alt="sceneCameraImage" />
                                <div className="scale-rect-container" onMouseMove={(e) => onHandleDrag(e)} onMouseUp={(e) => onHandleDragEnd(e)}>
                                    <div className="scale-handle" draggable="true" data-index={0} onMouseDown={(e) => onHandleStartDrag(e, 0)} style={{ top: (top * 100) + "%", left: (left * 100) + "%" }}></div>
                                    <div className="scale-handle" draggable="true" data-index={1} onMouseDown={(e) => onHandleStartDrag(e, 1)} style={{ top: (top * 100) + "%", left: (right * 100) + "%" }}></div>
                                    <div className="scale-handle" draggable="true" data-index={2} onMouseDown={(e) => onHandleStartDrag(e, 2)} style={{ top: (bottom * 100) + "%", left: (left * 100) + "%" }}></div>
                                    <div className="scale-handle" draggable="true" data-index={3} onMouseDown={(e) => onHandleStartDrag(e, 3)} style={{ top: (bottom * 100) + "%", left: (right * 100) + "%" }}></div>
                                    <div className="hotspot-item active" draggable="true" data-index={0} onMouseDown={(e) => onHandleStartDrag(e, -1)} style={{ top: (y * 100) + "%", left: (x * 100) + "%" }}></div>
                                    <div className="scale-rect" style={{ top: (top * 100) + "%", left: (left * 100) + "%", bottom: ((1 - bottom) * 100) + "%", right: ((1 - right) * 100) + "%" }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </Space>
    )
}


export const ModelHistoryDetailsButton: React.FC<{ model?: ModelViewDTO, item: AssetApprovalHistoryDTO }> = ({ model, item }) => {

    const [isOpen, setIsOpen] = useState(false);

    const showModal = useCallback(() => {
        setIsOpen(true);
    }, []);

    const handleOk = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleCancel = useCallback(() => {
        setIsOpen(false);
    }, []);

    const oldModel = useMemo(() => {
        if (item.assetData && isOpen) {
            return JSON.parse(item.assetData) as ModelViewDTO;
        }
        return undefined;
    }, [isOpen, item.assetData]);

    const diffs = useMemo(() => {
        if (item.assetData && isOpen) {
            var oldData = JSON.parse(item.assetData) as ModelViewDTO;
            let changeset = flattenChangeset(diff(oldData, model, { surfaces: 'name', placementpoints: 'name', metadata: 'name' }));

            const ignoredProperties = ["$.approvalStatus", "$.timestampCreate"];
            changeset = changeset.filter(e => !ignoredProperties.includes(e.path));

            return changeset;
        }
        return undefined;
    }, [isOpen, item.assetData, model]);

    return (<>
        <Button size='small' type='link' onClick={showModal}>Details</Button>

        <Modal open={isOpen} onOk={handleOk} onCancel={handleCancel} width={1000}>
            <p>
                Status changed from <b>{AssetApprovalStatusLabels[item.oldStatus as AssetApprovalStatus]}</b> to <b>{AssetApprovalStatusLabels[item.newStatus as AssetApprovalStatus]}</b> at {item.timestamp} by {item.username ?? "API"}
            </p>
            {item.comment && <p>
                Comment: <br />
                {item.comment}
            </p>}

            <Row>
                <Col span={12}>
                    <Title level={2}>Images</Title>
                    {item.images.map(e => <Image src={BASE_PATH + e} />)}


                    <Title level={2}>Data</Title>

                    {oldModel &&
                        <Descriptions layout='horizontal' size='small' bordered column={1} items={[
                            {
                                label: 'Title',
                                children: oldModel?.title
                            },
                            {
                                label: 'Filename',
                                children: oldModel?.filename
                            },
                            {
                                label: 'Name',
                                children: oldModel?.name
                            },
                            {
                                label: 'Tags',
                                children: oldModel?.tags?.map(e => <Tag>{e}</Tag>)
                            },
                            {
                                label: 'Meta data',
                                children: <Descriptions size='small' colon layout='horizontal' column={1} items={oldModel?.metadata?.map(e => ({ label: e.name, children: e.value }))} />
                            },
                            {
                                label: 'Surfaces',
                                children: oldModel?.surfaces?.map(surface => (
                                    <Descriptions style={{ marginBottom: 20 }} labelStyle={{ width: 175 }} bordered size='small' colon layout='horizontal' column={1} items={[
                                        {
                                            label: 'Label',
                                            children: surface.label
                                        },
                                        {
                                            label: 'Name',
                                            children: surface.name
                                        },
                                        {
                                            label: 'material selection filter',
                                            children: surface.materialSelectionFilter
                                        },
                                        {
                                            label: 'Object selector',
                                            children: surface.objectSelector
                                        }
                                    ]} />
                                ))
                            },
                            {
                                label: 'Placement points',
                                children: oldModel?.placementpoints?.map(point => (
                                    <Descriptions style={{ marginBottom: 20 }} labelStyle={{ width: 175 }} bordered size='small' colon layout='horizontal' column={1} items={[
                                        {
                                            label: 'Label',
                                            children: point.label
                                        },
                                        {
                                            label: 'Name',
                                            children: point.name
                                        },
                                        {
                                            label: 'Required',
                                            children: point.isRequired.toString()
                                        },
                                        {
                                            label: 'Model selection filter',
                                            children: point.modelSelectionFilter
                                        },
                                        {
                                            label: 'Use modelpack',
                                            children: point.useModelpack.toString()
                                        },
                                        {
                                            label: 'Object selector',
                                            children: point.objectSelector
                                        }
                                    ]} />
                                ))
                            },
                        ]} />
                    }


                </Col>
                <Col span={12}>
                    <Title>Diff with current data</Title>
                    {diffs?.map(e => <>
                        {e.type === "UPDATE" ? <Alert icon={<EditOutlined />} showIcon type='info' message={e.path} description={<><del>{JSON.stringify(e.oldValue)}</del> {JSON.stringify(e.value)}</>} /> : null}
                        {e.type === "ADD" ? <Alert icon={<PlusOutlined />} showIcon type='success' message={e.path} description={JSON.stringify(e.value)} /> : null}
                        {e.type === "REMOVE" ? <Alert icon={<DeleteOutlined />} showIcon type='error' message={e.path} description={<del>{JSON.stringify(e.value)}</del>} /> : null}
                    </>
                    )}
                </Col>
            </Row>
        </Modal>
    </>)
}

export default EditModel;